import { Table, TableBody, TableCell, TableRow } from '@/components/ui/table';
import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CustomerIdentityTableHeader } from './customer-identity-table-header';
import { CustomerIdentityTableRow } from './customer-identity-table-row';
import type { CustomerIdentityPreview, CustomerIdentityTenant } from './types/types';

type CustomerIdentityListProps = {
  isSearchActive?: boolean;
  onPreviewChange: (preview: CustomerIdentityPreview) => void;
  selectedTenantId?: string;
  tenants: CustomerIdentityTenant[];
};

export const CustomerIdentityList = ({
  isSearchActive = false,
  onPreviewChange,
  selectedTenantId,
  tenants,
}: CustomerIdentityListProps) => {
  const { t } = useTranslation();
  const [expandedTenantIds, setExpandedTenantIds] = useState<Set<string>>(new Set());

  const toggleTenant = (tenantId: string) => {
    setExpandedTenantIds((current) => {
      const next = new Set(current);

      if (next.has(tenantId)) {
        next.delete(tenantId);
      } else {
        next.add(tenantId);
      }

      return next;
    });
  };

  return (
    <div className="overflow-hidden rounded-sm border border-border">
      <Table className="w-full table-fixed bg-transparent">
        <CustomerIdentityTableHeader />

        <TableBody>
          {tenants.map((tenant) => {
            const hasSubTenants = tenant.subTenants.length > 0;
            const isExpanded =
              hasSubTenants && (isSearchActive || expandedTenantIds.has(tenant.id));

            return (
              <Fragment key={tenant.id}>
                <CustomerIdentityTableRow
                  tenant={tenant}
                  subTenantCount={isSearchActive ? 0 : tenant.subTenants.length}
                  isExpanded={isExpanded}
                  isSelected={selectedTenantId === tenant.id}
                  onPreviewChange={onPreviewChange}
                  onToggle={
                    hasSubTenants && !isSearchActive ? () => toggleTenant(tenant.id) : undefined
                  }
                />

                {isExpanded &&
                  tenant.subTenants.map((subTenant) => (
                    <CustomerIdentityTableRow
                      key={subTenant.id}
                      tenant={subTenant}
                      parentTenantName={tenant.name}
                      isSelected={selectedTenantId === subTenant.id}
                      onPreviewChange={onPreviewChange}
                    />
                  ))}
              </Fragment>
            );
          })}

          {!tenants.length && (
            <TableRow>
              <TableCell
                colSpan={5}
                className="h-24 text-center text-sm font-semibold text-text-secondary"
              >
                {t('noTenantsFound')}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
};
